import { DatabaseEnum } from "@Shape-Digital/kudzu-data/lib/types/common";
import { DateRange } from "@mui/x-date-pickers-pro";
import { useMemo, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { GridFilterModel, GridSortItem } from "@mui/x-data-grid-pro";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { createSupabaseClient } from "../../../hooks/useSupabase";
import { useAppContext } from "../../Unknown/AppContext";
import useTranslations from "../../Unknown/AppContext/useTranslations";
import { useUIContext } from "../../Unknown/UIContext";
import getDashboardAppointments, {
  getAllDashboardAppointments,
} from "./getAppointments";

type SortField =
  | "status"
  | "date"
  | "time"
  | "location"
  | "locationState"
  | "locationZip";

const isSortField = (field: string): field is SortField =>
  [
    "status",
    "date",
    "time",
    "location",
    "locationState",
    "locationZip",
  ].includes(field);

const mapSortModel = (
  sortModel: GridSortItem[],
): { field: SortField; ascending: boolean }[] => {
  return sortModel
    .filter((item) => isSortField(item.field))
    .map((item) => ({
      field: item.field as SortField,
      ascending: item.sort !== "desc",
    }));
};

const getStatuses = (role: string): DatabaseEnum["appointment_status"][] => {
  switch (role) {
    case "regional_admin":
    case "super_admin":
      return [
        "incomplete",
        "confirmed",
        "checked_in",
        "abandoned",
        "checkout",
        "intake_complete",
        "no_show",
        "canceled",
        "studies_unread",
        "studies_read",
        "reports_sent",
        "reports_error",
      ];
    default:
      return [];
  }
};

type PageCursor = { started_at: string; id: string };

export const useTableAppointments = (params: {
  dateRange?: DateRange<Date>;
  centerIds: string[];
  date: Date;
  filterModel: GridFilterModel;
  sortModel: GridSortItem[];
  paginationModel: { page: number; pageSize: number };
}) => {
  const {
    centerIds,
    date,
    dateRange,
    filterModel,
    sortModel,
    paginationModel,
  } = params;
  const supabase = useMemo(() => createSupabaseClient(), []);
  const translations = useTranslations();
  const { role: urlParamRole } = useParams();
  const queryClient = useQueryClient();

  const { centers } = useAppContext();

  const timezone = centers.length === 1 ? centers[0].timezone : null;

  const { setAlert } = useUIContext();

  const statuses = getStatuses(urlParamRole || "");

  // Cursor map: page N → cursor to use when fetching page N
  // Stored in a ref so queryFn reads the latest value without being in the query key
  const cursorsRef = useRef<Record<number, PageCursor>>({});

  // Reset cursor map whenever anything other than page number changes
  // (i.e. when the result set itself changes)
  const filterKeyRef = useRef("");
  const filterKey = JSON.stringify({
    centerIds,
    date,
    dateRange,
    filterModel,
    sortModel,
    urlParamRole,
  });
  if (filterKeyRef.current !== filterKey) {
    filterKeyRef.current = filterKey;
    cursorsRef.current = {};
  }

  // Count key excludes pagination and sort — stays cache-valid on page flip
  const countQueryKey = [
    "dashboard-appointments-count",
    { centerIds, date, dateRange, filterModel, urlParamRole },
  ];

  // Subscribe to the count cache (populated by the data query via setQueryData)
  const { data: cachedCount } = useQuery<number>({
    queryKey: countQueryKey,
    queryFn: () => Promise.resolve(0),
    enabled: false,
    staleTime: Infinity,
    gcTime: 300_000,
  });

  const { data, isPending, isFetching, isPlaceholderData, isError, refetch } =
    useQuery({
      queryKey: [
        "dashboard-appointments",
        {
          centerIds,
          date,
          dateRange,
          filterModel,
          sortModel,
          paginationModel,
          urlParamRole,
        },
      ],
      queryFn: async () => {
        const cursor =
          paginationModel.page > 0
            ? cursorsRef.current[paginationModel.page]
            : undefined;

        const result = await getDashboardAppointments({
          supabase,
          centerIds,
          date,
          statuses,
          dateRange,
          timezone,
          pagination: paginationModel,
          sort: mapSortModel(sortModel),
          filters: filterModel,
          cursor,
        });

        // Store cursor for the next page so subsequent fetches use keyset mode
        if (result.nextCursor) {
          cursorsRef.current = {
            ...cursorsRef.current,
            [paginationModel.page + 1]: result.nextCursor,
          };
        }

        // Cache count independently so it stays stable when only page changes
        queryClient.setQueryData(countQueryKey, result.count);
        return result;
      },
      placeholderData: keepPreviousData,
      staleTime: 30_000,
    });

  // Prefetch the next page while the user views the current one
  useEffect(() => {
    if (!data?.data?.length || data.data.length < paginationModel.pageSize)
      return;

    const nextPage = paginationModel.page + 1;
    // Cursor for the next page was stored by the queryFn above
    const nextCursor = cursorsRef.current[nextPage];

    queryClient.prefetchQuery({
      queryKey: [
        "dashboard-appointments",
        {
          centerIds,
          date,
          dateRange,
          filterModel,
          sortModel,
          paginationModel: { ...paginationModel, page: nextPage },
          urlParamRole,
        },
      ],
      queryFn: () =>
        getDashboardAppointments({
          supabase,
          centerIds,
          date,
          statuses,
          dateRange,
          timezone,
          pagination: { page: nextPage, pageSize: paginationModel.pageSize },
          sort: mapSortModel(sortModel),
          filters: filterModel,
          cursor: nextCursor,
        }),
      staleTime: 30_000,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (isError) {
      setAlert({
        isShown: true,
        message: translations.defaultError,
        severity: "error",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  const fetchAllForExport = useCallback(
    () =>
      getAllDashboardAppointments({
        supabase,
        centerIds,
        date,
        statuses,
        dateRange,
        timezone,
        sort: mapSortModel(sortModel),
        filters: filterModel,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      supabase,
      centerIds,
      date,
      statuses,
      dateRange,
      timezone,
      sortModel,
      filterModel,
    ],
  );

  return {
    appointments: data?.data ?? [],
    rowCount: cachedCount ?? data?.count ?? 0,
    isLoading: isPending,
    isFetching: isFetching && isPlaceholderData,
    refetch,
    fetchAllForExport,
  };
};
